import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetchJson } from '../../apiClient';
import './GrapeEscapeHome.css';

const unwrap = (data) => {
  const arr = Array.isArray(data) ? data : (data?.$values || []);
  return arr.filter(item => item && !item.$ref);
};

function GrapeEscapeHome({ instance, showMessage, onNavigate }) {
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [recentNotes, setRecentNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [profileResult, notesResult] = await Promise.allSettled([
        apiFetchJson(instance, '/api/wineprofile'),
        apiFetchJson(instance, '/api/tasting/notes'),
      ]);
      if (profileResult.status === 'fulfilled') setProfile(profileResult.value);
      if (notesResult.status === 'fulfilled') setRecentNotes(unwrap(notesResult.value).slice(0, 5));
    } catch (e) {
      console.error('Failed to load dashboard:', e);
      setError(t('grapeEscape.home.loadError', 'Failed to load dashboard data.'));
    } finally {
      setLoading(false);
    }
  }, [instance, t]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  if (loading) {
    return (
      <div className="ge-loading">
        <div className="ge-spinner" />
        <span>{t('grapeEscape.home.loading', 'Loading dashboard...')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ge-error">
        <span>{error}</span>
        <button onClick={loadDashboard}>↻</button>
      </div>
    );
  }

  const xp = profile?.xp || 0;
  const level = profile?.level || 1;
  const levelTitle = profile?.levelTitle || t('grapeEscape.home.novice', 'Novice');
  const xpForNext = profile?.xpForNextLevel || 100;
  const xpProgress = xpForNext > 0 ? Math.min((xp / xpForNext) * 100, 100) : 0;

  return (
    <div className="ge-home">
      {/* XP Bar */}
      <div className="ge-xp-card ge-card">
        <div className="ge-xp-header">
          <span className="ge-level-badge">
            {t('grapeEscape.home.level', 'Level')} {level}
          </span>
          <span className="ge-level-title">{levelTitle}</span>
          <span className="ge-xp-text">{xp} / {xpForNext} XP</span>
        </div>
        <div className="ge-xp-bar">
          <div className="ge-xp-fill" style={{ width: `${xpProgress}%` }} />
        </div>
      </div>

      {/* Quick Stats */}
      <div className="ge-stats-grid">
        <div className="ge-stat-card ge-card">
          <span className="ge-stat-icon">🍾</span>
          <span className="ge-stat-value">{profile?.bottlesInCellar ?? 0}</span>
          <span className="ge-stat-label">{t('grapeEscape.home.bottles', 'Bottles')}</span>
        </div>
        <div className="ge-stat-card ge-card">
          <span className="ge-stat-icon">📝</span>
          <span className="ge-stat-value">{profile?.totalTastings ?? 0}</span>
          <span className="ge-stat-label">{t('grapeEscape.home.tastings', 'Tastings')}</span>
        </div>
        <div className="ge-stat-card ge-card">
          <span className="ge-stat-icon">🔥</span>
          <span className="ge-stat-value">{profile?.currentStreak ?? 0}</span>
          <span className="ge-stat-label">{t('grapeEscape.home.streak', 'Day Streak')}</span>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="ge-quick-actions">
        <button className="ge-btn-primary ge-action-btn" onClick={() => onNavigate('cellar')}>
          🍷 {t('grapeEscape.home.addWine', 'Add Wine')}
        </button>
        <button className="ge-btn-secondary ge-action-btn" onClick={() => onNavigate('taste')}>
          📝 {t('grapeEscape.home.newTasting', 'New Tasting')}
        </button>
        <button className="ge-btn-secondary ge-action-btn" onClick={() => onNavigate('taste')}>
          👥 {t('grapeEscape.home.startSession', 'Start Session')}
        </button>
      </div>

      {/* Recent Tasting Notes */}
      <div className="ge-section">
        <div className="ge-section-header">
          <h3>{t('grapeEscape.home.recentNotes', 'Recent Tasting Notes')}</h3>
        </div>
        {recentNotes.length === 0 ? (
          <div className="ge-empty">
            {t('grapeEscape.home.noNotes', 'No tasting notes yet. Start your wine journey!')}
          </div>
        ) : (
          <div className="ge-recent-notes">
            {recentNotes.map(note => (
              <div key={note.id} className="ge-note-card ge-card">
                <div className="ge-note-header">
                  <span className="ge-note-wine">{note.wineName || 'Unknown wine'}</span>
                  <span className="ge-note-rating">{'⭐'.repeat(Math.min(note.rating || 0, 5))}</span>
                </div>
                <div className="ge-note-meta">
                  <span>{note.vintage ? `${note.vintage}` : ''}</span>
                  <span>{note.tastingDate ? new Date(note.tastingDate).toLocaleDateString() : ''}</span>
                </div>
                {note.notes && <p className="ge-note-text">{note.notes}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default GrapeEscapeHome;
